import React, { useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, Navigate } from 'react-router-dom';

const RouteGuard = ({
  children,
  requiredRoles = [],
  requireAuth = true,
  redirectTo = '/login-screen'
}) => {
  const { user, session, loading, hasRole } = useAuth();
  const location = useLocation();
  const hasAuthenticatedRef = useRef(false);

  // Marcar como autenticado quando tiver user ou session
  useEffect(() => {
    if (user || session) {
      hasAuthenticatedRef.current = true;
    }
  }, [user, session]);

  // Logs para debug
  console.log('🔍 RouteGuard executando...');
  console.log('📝 Props:', { requireAuth, requiredRoles, redirectTo });
  console.log('📝 Estado atual:', { user, session, loading, hasRole });
  console.log('📝 Localização atual:', location.pathname);

  // Show loading state only on initial load, not on subsequent navigations
  if (loading && !hasAuthenticatedRef.current) {
    console.log('⏳ RouteGuard: Mostrando loading (primeira vez)...');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Check if authentication is required
  if (requireAuth && !user && !session) {
    console.log('🚫 RouteGuard: Usuário não autenticado, redirecionando para:', redirectTo);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Supabase restores the authenticated user before the tenant role. Avoid
  // redirecting a valid admin during that short hydration window; direct
  // links such as /admin/lesson-reviews must remain stable on reload.
  if (requiredRoles.length > 0 && user && !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Validando acesso administrativo...</p>
        </div>
      </div>
    );
  }

  // Check if specific roles are required
  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    console.log('🚫 RouteGuard: Usuário não tem role necessário, redirecionando para dashboard');
    console.log('🔍 Role do usuário:', session?.userTenant?.role);
    console.log('🔍 Roles necessários:', requiredRoles);
    // Redirect to dashboard if user doesn't have required role
    return <Navigate to="/dashboard-home" replace />;
  }

  // User is authenticated and has required role (if any)
  console.log('✅ RouteGuard: Usuário autenticado, permitindo acesso');
  return <>{children}</>;
};

export default RouteGuard;
